import { Box, Cell, IconButton, FormField, Layout, Text, ToggleSwitch } from '@wix/design-system';
import { ChevronLeftSmall, ChevronRightSmall } from '@wix/wix-ui-icons-common/odeditor';
import { DomainSearchSuiteModule } from '@wix/domains-search-suite';
import React, { useState } from 'react';
import { DomainSearchGateModule } from '../../domainSearchGate.module';
import { TldOfferCard, TldOfferCardSkeleton } from '../card';
import { TldOfferGrid } from './TldOfferGrid';
import { TLD_OFFER_WIDGET_BI_ORIGIN, TLD_OFFER_WIDGET_BI_TYPE, TLD_OFFERS_COLUMNS, TLD_OFFERS_PAGE_SIZE, TldStripComponentName } from '../tldOfferWidget.constants';
import { TldOfferWidgetModule } from '../tldOfferWidget.module';
import { getExpandPageNumber, hasLoadedPage, shouldLoadNextBatch } from '../tldOfferWidget.utils';
import { useTldOffersData } from '../useTldOffersData';
import { useTldStripImpression } from '../useTldStripImpression';
export const tldOfferExpandDataHooks = {
  root: 'tld-offer-expand',
  toggle: 'tld-offer-expand-toggle',
  card: 'tld-offer-expand-card',
  skeleton: 'tld-offer-expand-skeleton',
  previous: 'tld-offer-expand-previous',
  next: 'tld-offer-expand-next'
};
const getPageOffers = (offers, page) => offers.slice(page * TLD_OFFERS_PAGE_SIZE, (page + 1) * TLD_OFFERS_PAGE_SIZE);
export const TldOfferExpand = _ref => {
  let domain = _ref.domain;
  const _useState = useState(false),
    isExpanded = _useState[0],
    setIsExpanded = _useState[1];
  const _useState2 = useState(0),
    page = _useState2[0],
    setPage = _useState2[1];
  const _useTldOffersData = useTldOffersData(domain),
    sld = _useTldOffersData.sld,
    offers = _useTldOffersData.offers,
    isLoading = _useTldOffersData.isLoading,
    isFolded = _useTldOffersData.isFolded,
    isEligible = _useTldOffersData.isEligible,
    hasNextPage = _useTldOffersData.hasNextPage,
    isFetchingNextPage = _useTldOffersData.isFetchingNextPage,
    fetchNextPage = _useTldOffersData.fetchNextPage,
    pricesByDomain = _useTldOffersData.pricesByDomain,
    isPricingLoading = _useTldOffersData.isPricingLoading;
  const _DomainSearchGateModu = DomainSearchGateModule.internals.useTranslations(),
    t = _DomainSearchGateModu.t;
  const _DomainSearchSuiteMod = DomainSearchSuiteModule.SDK.useSuggestionActionResolver(),
    resolveActionForSuggestion = _DomainSearchSuiteMod.resolveActionForSuggestion;
  const StripContainer = TldOfferWidgetModule.SDK.Layouts.StripContainer;
  const pageOffers = getPageOffers(offers, page);
  const missingSlots = isFetchingNextPage ? TLD_OFFERS_PAGE_SIZE - pageOffers.length : 0;
  const collapsedTlds = isFolded ? [] : pageOffers.slice(0, TLD_OFFERS_COLUMNS);
  const viewedTlds = isExpanded ? pageOffers : collapsedTlds;
  const impressionRef = useTldStripImpression({
    domain,
    sld,
    componentName: TldStripComponentName.EXPAND,
    pageNumber: getExpandPageNumber({
      isExpanded,
      isFolded,
      page
    }),
    tlds: viewedTlds,
    enabled: !isLoading && isEligible && missingSlots === 0 && viewedTlds.length > 0
  });
  if (!isLoading && !isEligible) {
    return null;
  }
  const goToPage = nextPage => {
    setPage(nextPage);
    if (shouldLoadNextBatch({
      page: nextPage,
      offersCount: offers.length,
      hasMore: hasNextPage,
      isFetching: isFetchingNextPage
    })) {
      fetchNextPage();
    }
  };
  const handleGetItClick = async (tld, position) => {
    DomainSearchSuiteModule.internals.bi.onGetItClick == null || DomainSearchSuiteModule.internals.bi.onGetItClick({
      domain: `${sld}.${tld}`,
      position,
      type: TLD_OFFER_WIDGET_BI_TYPE,
      origin: TLD_OFFER_WIDGET_BI_ORIGIN
    });
    await resolveActionForSuggestion(`${sld}.${tld}`, false);
  };
  const cells = [...pageOffers.map((tld, index) => /*#__PURE__*/React.createElement(Cell, {
    key: tld,
    span: 1
  }, /*#__PURE__*/React.createElement(TldOfferCard, {
    domainName: `${sld}.${tld}`,
    pricing: pricesByDomain.get(`${sld}.${tld}`),
    isPricingLoading: isPricingLoading,
    onGetItClick: () => handleGetItClick(tld, page * TLD_OFFERS_PAGE_SIZE + index + 1),
    dataHook: tldOfferExpandDataHooks.card
  }))), ...Array.from({
    length: missingSlots
  }, (_, index) => /*#__PURE__*/React.createElement(Cell, {
    key: `skeleton-${index}`,
    span: 1
  }, /*#__PURE__*/React.createElement(TldOfferCardSkeleton, {
    dataHook: tldOfferExpandDataHooks.skeleton
  })))];
  const pinnedCells = isFolded ? [] : cells.slice(0, TLD_OFFERS_COLUMNS);
  const expandableCells = isFolded ? cells : cells.slice(TLD_OFFERS_COLUMNS);
  const isNextDisabled = isLoading || !hasLoadedPage(page + 1, offers.length) && (!hasNextPage || isFetchingNextPage);
  return /*#__PURE__*/React.createElement(StripContainer, {
    containerRef: impressionRef,
    dataHook: tldOfferExpandDataHooks.root
  }, /*#__PURE__*/React.createElement(Box, {
    verticalAlign: "middle",
    gap: "6px",
    width: "100%"
  }, /*#__PURE__*/React.createElement(Box, {
    flexGrow: 1
  }, /*#__PURE__*/React.createElement(Text, {
    size: "medium",
    weight: "bold"
  }, t('domains_search_gate.results_optimisations.tld_strip.title', {
    domainSLD: sld
  }))), /*#__PURE__*/React.createElement(FormField, {
    label: t('domains_search_gate.results_optimisations.tld_strip.view_all'),
    labelPlacement: "right",
    labelSize: "small",
    stretchContent: false
  }, /*#__PURE__*/React.createElement(ToggleSwitch, {
    size: "medium",
    checked: isExpanded,
    disabled: isLoading,
    onChange: () => {
      setIsExpanded(!isExpanded);
      if (isExpanded) {
        setPage(0);
      } else {
        goToPage(0);
      }
    },
    dataHook: tldOfferExpandDataHooks.toggle
  }))), isLoading ? /*#__PURE__*/React.createElement(Box, {
    marginTop: "SP6",
    direction: "vertical"
  }, /*#__PURE__*/React.createElement(Layout, {
    cols: TLD_OFFERS_COLUMNS,
    gap: "18px 16px"
  }, Array.from({
    length: TLD_OFFERS_COLUMNS
  }, (_, index) => /*#__PURE__*/React.createElement(Cell, {
    key: index,
    span: 1
  }, /*#__PURE__*/React.createElement(TldOfferCardSkeleton, {
    dataHook: tldOfferExpandDataHooks.skeleton
  }))))) : /*#__PURE__*/React.createElement(React.Fragment, null, pinnedCells.length > 0 && /*#__PURE__*/React.createElement(Box, {
    marginTop: "SP6",
    direction: "vertical"
  }, /*#__PURE__*/React.createElement(TldOfferGrid, null, pinnedCells)), /*#__PURE__*/React.createElement(Box, {
    display: "grid",
    gridTemplateRows: isExpanded ? '1fr' : '0fr',
    transition: "grid-template-rows 350ms cubic-bezier(0.4, 0, 0.2, 1)"
  }, /*#__PURE__*/React.createElement(Box, {
    display: "block",
    overflow: "hidden",
    visibility: isExpanded ? 'visible' : 'hidden',
    transition: "visibility 350ms"
  }, /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    gap: "SP6",
    paddingTop: isFolded ? 'SP6' : '18px'
  }, expandableCells.length > 0 && /*#__PURE__*/React.createElement(TldOfferGrid, null, expandableCells), /*#__PURE__*/React.createElement(Box, {
    align: "right",
    gap: "6px"
  }, /*#__PURE__*/React.createElement(IconButton, {
    size: "small",
    priority: "secondary",
    skin: "dark",
    disabled: isLoading || page === 0,
    onClick: () => goToPage(page - 1),
    dataHook: tldOfferExpandDataHooks.previous
  }, /*#__PURE__*/React.createElement(ChevronLeftSmall, null)), /*#__PURE__*/React.createElement(IconButton, {
    size: "small",
    priority: "secondary",
    skin: "dark",
    disabled: isNextDisabled,
    onClick: () => goToPage(page + 1),
    dataHook: tldOfferExpandDataHooks.next
  }, /*#__PURE__*/React.createElement(ChevronRightSmall, null))))))));
};
//# sourceMappingURL=TldOfferExpand.js.map